import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Camera, Send, X, ShieldCheck, Truck, Headphones, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, photoUrl } from "@/lib/api";
import Seo from "@/components/Seo";

export default function FindReplacement() {
  const [params] = useSearchParams();
  const prefillProduct = params.get("product");
  const prefillSku = params.get("sku");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    brand_model: prefillProduct ? `${prefillProduct}${prefillSku ? ` (SKU: ${prefillSku})` : ""}` : "",
    message: prefillProduct ? "I'd like to check this is the correct replacement for my existing part (details/photos below)." : "",
  });
  const [photos, setPhotos] = useState([]); // {path}
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (photos.length + files.length > 6) {
      toast.error("You can upload up to 6 photos.");
      return;
    }
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/reviews/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => [...prev, { path: data.path }]);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not upload image.");
      }
    }
    setUploading(false);
  };

  const removePhoto = (path) => setPhotos((prev) => prev.filter((p) => p.path !== path));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/replacement-request", {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        brand_model: form.brand_model || undefined,
        message: form.message || undefined,
        photos: photos.map((p) => p.path),
      });
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", brand_model: "", message: "" });
      setPhotos([]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="find-replacement-page">
      <Seo
        title="Find My Replacement Part"
        description="Not sure which heating control or valve you need? Send us a photo or the details of your old part and our specialists will identify the correct replacement — free tracked UK delivery (Royal Mail Tracked 48)."
      />
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6"><ArrowLeft className="w-4 h-4" /> Back to shop</Link>
      <span className="block text-overline text-primary">Not sure what you need?</span>
      <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Find My Replacement</h1>
      <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
        Replacing an old valve, thermostat or controller? Send us a photo and any details you have — a part number, brand or model — and our heating specialists will identify the correct replacement and get back to you, usually within one working day.
      </p>

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 mt-8">
        <form onSubmit={submit} className="bg-card border border-border rounded-sm p-6 space-y-4" data-testid="replacement-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name" v={form.name} on={set("name")} tid="rep-name" required />
            <Field label="Email" type="email" v={form.email} on={set("email")} tid="rep-email" required />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Phone (optional)" v={form.phone} on={set("phone")} tid="rep-phone" />
            <Field label="Existing part / brand / model (optional)" v={form.brand_model} on={set("brand_model")} tid="rep-brand" />
          </div>
          <label className="block">
            <span className="text-sm font-medium">Describe what you need</span>
            <textarea value={form.message} onChange={set("message")} data-testid="rep-message" rows={5}
              placeholder="e.g. My old motorised valve has stopped working — the label says ACL Lifestyle. What replaces it?"
              className="mt-1.5 w-full px-3 py-2.5 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
          </label>

          {/* PHOTO UPLOAD */}
          <div>
            <span className="text-sm font-medium">Photos of your existing part (recommended)</span>
            <div className="mt-2 flex flex-wrap gap-3">
              {photos.map((p) => (
                <div key={p.path} className="relative w-20 h-20 rounded-sm border border-border overflow-hidden bg-white">
                  <img src={photoUrl(p.path)} alt="Uploaded part" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(p.path)} data-testid="rep-photo-remove"
                    className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {photos.length < 6 && (
                <label data-testid="rep-photo-add"
                  className="w-20 h-20 grid place-items-center rounded-sm border-2 border-dashed border-border text-muted-foreground cursor-pointer hover:border-primary hover:text-primary transition-colors">
                  <Camera className="w-5 h-5" />
                  <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
                </label>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{uploading ? "Uploading…" : "JPG, PNG or WEBP — up to 6 photos, max 6MB each. A clear photo of the part and any label helps us a lot."}</p>
          </div>

          <button type="submit" disabled={busy || uploading} data-testid="rep-submit"
            className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors">
            <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send to our specialists"}
          </button>
          <p className="text-xs text-muted-foreground pt-1">
            When we reply, please check your junk/spam folder in case our response lands there.
          </p>
        </form>

        <aside className="space-y-4">
          <div className="bg-card border border-border rounded-sm p-6">
            <h2 className="font-display font-bold text-lg">How it works</h2>
            <ol className="mt-3 space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3"><span className="grid place-items-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold shrink-0">1</span> Send a photo and any details of your old part.</li>
              <li className="flex gap-3"><span className="grid place-items-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold shrink-0">2</span> Our specialists identify the correct replacement.</li>
              <li className="flex gap-3"><span className="grid place-items-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold shrink-0">3</span> We reply with the right product and a price — free tracked UK delivery (Royal Mail Tracked 48).</li>
            </ol>
          </div>
          <div className="bg-card border border-border rounded-sm p-6 space-y-3 text-sm">
            {[{ i: ShieldCheck, t: "Genuine, fully warranted parts" }, { i: Truck, t: "Free tracked delivery on every order" }, { i: Headphones, t: "Real heating specialists, Mon–Fri" }].map((x) => (
              <div key={x.t} className="flex items-center gap-2.5 font-medium"><x.i className="w-5 h-5 text-primary shrink-0" /> {x.t}</div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", tid, required }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <input type={type} value={v} onChange={on} data-testid={tid} required={required}
        className="mt-1.5 w-full h-11 px-3 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
    </label>
  );
}
